import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';
import { MachinePicker } from './MachinePicker.jsx';
import { ISSUE_STATUS_LABEL, SEVERITY_LABEL } from './maintenance-shared.js';

const trimOrNull = (v) => {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
};

/**
 * Used for both creating a new issue and editing an existing one.
 * Pass `issue` to switch to edit mode (PATCH instead of POST, status field
 * appears, machine picker is locked since the API doesn't support reassigning).
 */
export const NewIssueModal = ({ open, onClose, onCreated, onSaved, defaultMachine, issue }) => {
  const editing = !!issue;
  const { data: categories } = useApi('/maintenance/issue-categories');
  const { data: contacts } = useApi('/contacts?tag=technician');

  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [machine, setMachine] = useState({ printerId: null, equipmentId: null });
  const [categoryId, setCategoryId] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [status, setStatus] = useState('open');
  const [affectsProduction, setAffectsProduction] = useState(false);
  const [assigneeContactId, setAssigneeContactId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setTitle(issue.title ?? '');
      setDetails(issue.details ?? '');
      setCategoryId(issue.categoryId ? String(issue.categoryId) : '');
      setSeverity(issue.severity ?? 'medium');
      setStatus(issue.status ?? 'open');
      setAffectsProduction(!!issue.affectsProduction);
      setAssigneeContactId(issue.assigneeContactId ? String(issue.assigneeContactId) : '');
      setMachine({ printerId: issue.printerId, equipmentId: issue.equipmentId });
    } else {
      setTitle(''); setDetails(''); setCategoryId(''); setSeverity('medium'); setStatus('open');
      setAffectsProduction(false); setAssigneeContactId('');
      setMachine(defaultMachine ?? { printerId: null, equipmentId: null });
    }
  }, [open, editing, issue, defaultMachine]);

  if (!open) return null;

  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        const updated = await api.patch(`/maintenance/issues/${issue.id}`, {
          title:             title.trim(),
          details:           trimOrNull(details),
          categoryId:        categoryId ? Number(categoryId) : null,
          severity,
          status,
          affectsProduction,
          assigneeContactId: assigneeContactId ? Number(assigneeContactId) : null,
        });
        onSaved?.(updated);
      } else {
        const created = await api.post('/maintenance/issues', {
          title:             title.trim(),
          details:           trimOrNull(details),
          categoryId:        categoryId ? Number(categoryId) : null,
          severity,
          affectsProduction,
          printerId:         machine.printerId,
          equipmentId:       machine.equipmentId,
          assigneeContactId: assigneeContactId ? Number(assigneeContactId) : null,
        });
        onCreated?.(created);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: '#E63B7A' }}>
            {editing ? 'Edit issue' : 'New issue'}
          </p>
        </div>

        <div class="space-y-4 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Title</p>
            <input
              autoFocus
              type="text"
              value={title}
              onInput={(e) => setTitle(e.currentTarget.value)}
              placeholder="e.g. Yellow ink offline on WT7900"
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
              Machine
              <span class="ml-2 font-medium text-text-muted">
                · {editing ? 'locked — recreate to change' : 'optional — leave blank for a general issue'}
              </span>
            </p>
            <MachinePicker value={machine} onChange={setMachine} disabled={editing} />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Severity</p>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                {Object.entries(SEVERITY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Category</p>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                <option value="">—</option>
                {(categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          {editing && (
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Status</p>
              <select
                value={status}
                onChange={(e) => setStatus(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              >
                {Object.entries(ISSUE_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          )}

          <label class="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={affectsProduction}
              onChange={(e) => setAffectsProduction(e.currentTarget.checked)}
              class="h-4 w-4 rounded border-border-strong bg-bg-card"
            />
            <span class="text-[13px] font-medium text-text-body">Affects production (surfaces on the machine's page as a banner)</span>
          </label>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
              External technician <span class="font-medium text-text-muted">· optional</span>
            </p>
            <select
              value={assigneeContactId}
              onChange={(e) => setAssigneeContactId(e.currentTarget.value)}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            >
              <option value="">—</option>
              {(contacts ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ''}</option>)}
            </select>
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Details</p>
            <textarea
              rows={4}
              value={details}
              onInput={(e) => setDetails(e.currentTarget.value)}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
            </p>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !title.trim()}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Open issue'}
          </Button>
        </div>
      </div>
    </div>
  );
};
